import styled from '@emotion/styled'
import * as Popover from '@radix-ui/react-popover'
import { FC, ReactNode } from 'react'

import { ShiftActivity, ShiftHours } from '@bemlo/enums'
import { DayjsShift, getShiftHours } from '@bemlo/schedule'
import { Enumerable } from '@bemlo/utils'

import { CellStatus, useCellTheme } from './useCellTheme'
import { Color, theme } from '../../../../constants'
import {
  Checkbox,
  Div,
  Flex,
  Icon,
  IconName,
  SHIFT_ACTIVITY_COLORS,
} from '../../../../elements'
import { TooltipIcon } from '../../../../molecules/TooltipIcon'
import { useTranslations } from '../../../../translate'
import { ShiftCrossSection } from '../../ShiftCrossSection'
import { ScheduleZoom } from '../types'
import { useSchedule, useSelectedShifts } from '../useSchedule'
import { ChannelIndicators } from './ShiftChannelIndicators'

const WARNING_CSS = {
  position: 'absolute',
  left: -17,
  // Center vertically
  top: '50%',
  transform: 'translateY(-50%)',
} as const

const RelativeWrapper = styled(Div)<{ position: ShiftPosition }>(
  ({ position }) => ({
    position: 'relative',
    width: '100%',
    height: position === 'stretch' ? '100%' : 'auto',
    margin: position === 'center' ? `auto 0px` : undefined,
  }),
)

const PopoverContentWrapper = styled(Popover.Content)({
  zIndex: theme.zIndex.popover,
})

type ShiftDesign = {
  label?: string
  backgroundColor: string
  color: Color
  iconColor: string
  icon: IconName
  shiftHours: ShiftHours
  borderColor?: string
  borderWidth?: number
  borderStyle?: 'solid' | 'dashed' | 'dotted'
}

export const SHIFT_HOURS: Record<ShiftHours, ShiftDesign> = {
  [ShiftHours.DAY]: {
    backgroundColor: Color.LIGHTEST_BLUE,
    color: Color.CHARCOAL,
    iconColor: Color.BLUE,
    icon: 'SunIcon',
    shiftHours: ShiftHours.DAY,
  },
  [ShiftHours.EVENING]: {
    backgroundColor: '#FFF7F7',
    color: Color.CHARCOAL,
    iconColor: Color.RED,
    icon: 'EveningIcon',
    shiftHours: ShiftHours.EVENING,
  },
  [ShiftHours.LONG]: {
    backgroundColor: '#FFF7F7',
    color: Color.CHARCOAL,
    iconColor: Color.PINK,
    icon: 'DayEveningIcon',
    shiftHours: ShiftHours.LONG,
  },
  [ShiftHours.NIGHT]: {
    backgroundColor: '#F8EFFF',
    color: Color.CHARCOAL,
    iconColor: Color.VIOLET,
    icon: 'NightIcon',
    shiftHours: ShiftHours.NIGHT,
  },
}

export const useShiftActivityDesign = (
  activity: ShiftActivity,
  shiftHoursDesign: ShiftDesign,
  shift: DayjsShift,
): ShiftDesign => {
  switch (activity) {
    case ShiftActivity.FLEX:
      return {
        backgroundColor: SHIFT_ACTIVITY_COLORS.FLEX.background,
        color: Color.CHARCOAL,
        iconColor: Color.CHARCOAL,
        icon: SHIFT_ACTIVITY_COLORS.FLEX.icon,
        shiftHours: shiftHoursDesign.shiftHours,
      }
    case ShiftActivity.TEAM_MEETING:
      return {
        backgroundColor: SHIFT_ACTIVITY_COLORS.TEAM_MEETING.background,
        color: Color.CHARCOAL,
        iconColor: Color.CHARCOAL,
        icon: SHIFT_ACTIVITY_COLORS.TEAM_MEETING.icon,
        shiftHours: shiftHoursDesign.shiftHours,
      }
    case ShiftActivity.TRAINING:
      return {
        backgroundColor: SHIFT_ACTIVITY_COLORS.TRAINING.background,
        color: Color.WHITE,
        iconColor: Color.WHITE,
        icon: SHIFT_ACTIVITY_COLORS.TRAINING.icon,
        shiftHours: shiftHoursDesign.shiftHours,
      }
    case ShiftActivity.INTRODUCTION:
      return {
        backgroundColor: SHIFT_ACTIVITY_COLORS.INTRODUCTION.background,
        color: Color.WHITE,
        iconColor: Color.WHITE,
        icon: SHIFT_ACTIVITY_COLORS.INTRODUCTION.icon,
        shiftHours: shiftHoursDesign.shiftHours,
      }
    case ShiftActivity.OTHER:
      return {
        backgroundColor: SHIFT_ACTIVITY_COLORS.OTHER.background,
        color: Color.WHITE,
        iconColor: Color.WHITE,
        icon: SHIFT_ACTIVITY_COLORS.OTHER.icon,
        label: shift.comment,
        shiftHours: shiftHoursDesign.shiftHours,
      }
    case ShiftActivity.WORK:
      return shiftHoursDesign
    default:
      return shiftHoursDesign
  }
}

const useShiftDesign = (status: CellStatus, shift: DayjsShift): ShiftDesign => {
  const t = useTranslations()
  const shiftHours = getShiftHours(shift)
  const shiftHoursDesign = SHIFT_HOURS[shiftHours]
  const shiftActivityDesign = useShiftActivityDesign(
    shift.activity,
    shiftHoursDesign,
    shift,
  )
  const isWork = shift.activity === ShiftActivity.WORK
  const isFlex = shift.activity === ShiftActivity.FLEX
  const cellDesign = useCellTheme()

  if (status === 'BOOKED') {
    return shiftActivityDesign
  }

  if (status === 'DRAFT') {
    return {
      ...shiftActivityDesign,
      iconColor: isWork
        ? shiftActivityDesign.iconColor
        : isFlex
          ? Color.BRIGHT_YELLOW
          : shiftActivityDesign.backgroundColor,
      backgroundColor: Color.BACKGROUND_GREY,
      borderStyle: 'dashed',
      label: t.$shiftActivity(shift.activity),
    }
  }

  if (status === 'CANCELLED') {
    return {
      label: t.shifts.cancelled(),
      backgroundColor: Color.WHITE,
      color: Color.CHARCOAL,
      iconColor: Color.RED,
      icon: 'XIcon',
      shiftHours,
    }
  }

  if (status === 'SUGGESTED') {
    return {
      label: t.shifts.suggested(),
      backgroundColor: Color.YELLOW,
      color: Color.CHARCOAL,
      iconColor: Color.CHARCOAL,
      icon: shiftHoursDesign.icon,
      shiftHours,
    }
  }

  const { background, color, label, iconProps } = cellDesign[status]

  const theme = {
    label,
    backgroundColor: background,
    color,
    iconColor: iconProps.color,
    icon: iconProps.name,
    shiftHours,
  }

  if (status === 'VACANT') {
    return {
      ...theme,
      label: undefined,
      iconColor: shiftHoursDesign.iconColor,
      icon: shiftHoursDesign.icon,
    }
  }

  if (status === 'ACCEPTED') {
    return {
      ...theme,
      icon: shiftHoursDesign.icon,
    }
  }

  if (status === 'PRESET') {
    return {
      ...theme,
      borderWidth: 2,
      iconColor: shiftHoursDesign.iconColor,
      icon: shiftHoursDesign.icon,
    }
  }

  if (status === 'ACTIVE_PRESET') {
    return {
      ...shiftHoursDesign,
      borderColor: shiftHoursDesign.iconColor,
      borderWidth: 2,
      label,
    }
  }

  return theme
}

type ShiftPosition = 'center' | 'stretch'

type CardProps = {
  hasOnCall: boolean
  hoverable: boolean
  selected: boolean
  selectable: boolean
  isUrgent: boolean | undefined
  isAbsent: boolean | undefined
  isAtOtherUnit: boolean | undefined
  zoom: ScheduleZoom
  position: ShiftPosition
} & ShiftDesign

const Card = styled.div<CardProps>(
  ({
    backgroundColor,
    color,
    borderColor,
    borderWidth,
    borderStyle,
    hasOnCall,
    hoverable,
    selected,
    selectable,
    isUrgent,
    isAbsent,
    isAtOtherUnit,
    zoom,
    position,
  }) => ({
    // Remove all button styles
    unset: 'all',

    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    textAlign: 'left',
    backgroundColor,
    color,
    borderWidth: borderWidth ?? 1,
    borderStyle: borderStyle ?? 'solid',
    borderColor: borderColor ?? 'rgba(0, 0, 0, 0.2)',
    height: 'min-content',
    ...(position === 'stretch' && { height: '100%', justifyContent: 'center' }),
    ...(position === 'center' && {
      justifyContent: 'center',
      margin: `auto 0px`,
    }),
    width: '100%',
    borderRadius: theme.border.radius.normal,
    paddingTop: theme.spacing[2],
    paddingBottom: hasOnCall ? undefined : theme.spacing[2],
    paddingLeft: 0,
    paddingRight: 0,
    ...(isUrgent && {
      border: `1px solid ${Color.RED}`,
      borderLeft: zoom !== 'month' ? `19px solid ${Color.RED}` : undefined,
    }),
    ...(isAbsent && {
      border: `1px solid ${Color.CHARCOAL}`,
      borderLeft: zoom !== 'month' ? `19px solid ${Color.CHARCOAL}` : undefined,
    }),
    ...(isAtOtherUnit && {
      filter: 'grayscale(100%)',
    }),
    ...(selectable && {
      cursor: 'pointer',
    }),

    '.checkbox': {
      display: selected ? 'block' : 'none',
      zIndex: 2,
    },

    '.icon': {
      display: selected ? 'none' : undefined,
    },

    ':hover': {
      ...(hoverable && {
        cursor: 'pointer',
        filter: 'brightness(0.95)',
      }),

      ...(selectable && {
        '.checkbox': {
          display: 'block',
        },
        '.icon': {
          display: 'none',
        },
      }),
    },
  }),
)

const MonthContent = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
})

const Content = styled.div({
  boxSizing: 'border-box',
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `0px ${theme.spacing['2.5']}px`,
})

const StyledChannelIndicators = styled(ChannelIndicators)({
  marginLeft: -theme.spacing[1],
  marginRight: theme.spacing[1],
})

const TextColumn = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: theme.spacing[0.5],
  userSelect: 'none',
  marginRight: 'auto',
})

const Header = styled.span({
  fontWeight: theme.font.weight.medium,
  fontSize: 12,
})

const Subtitle = styled.span({
  fontSize: 10,
  opacity: 0.75,
})

const OnCallPadding = styled.div({
  paddingTop: theme.spacing[1],
  paddingBottom: theme.spacing[1],
  paddingLeft: theme.spacing[1.5],
  paddingRight: theme.spacing[1.5],
  width: '100%',
  boxSizing: 'border-box',
})

const AbsoluteBadge = styled(Div)<{ background: Color; color: Color }>(
  ({ background, color }) => ({
    background,
    color,
    borderRadius: theme.border.radius.normal,
    position: 'absolute',
    top: `-${theme.border.width.thin}px`,
    right: `-${theme.border.width.thin}px`,
    fontWeight: theme.font.weight.book,
    padding: `${theme.spacing['0.5']}px ${theme.spacing['1.5']}px`,
    lineHeight: 1,
    select: 'none',
    span: {
      display: 'block',
      fontSize: theme.font.size['6.5'],
      letterSpacing: -2,
    },
  }),
)

type NewShiftProps = {
  status: CellStatus
  isPublished?: boolean
  isTendered?: boolean
  shiftIds: string[]
  shift: DayjsShift
  unitName?: string
  interestedCandidates?: number
  isAcknowledged?: boolean
  isUrgent?: boolean
  isAbsent?: boolean
  isAtOtherUnit?: boolean
  unselectable?: boolean
  error?: ReactNode
  onClick?: (shiftId: Enumerable<string>) => void
  onDelete?: () => void
  popoverContent?: FC<{ shiftId: Enumerable<string> }>
  position?: ShiftPosition
}

export const NewShift = (props: NewShiftProps) => {
  const t = useTranslations()
  const style = useShiftDesign(props.status, props.shift)
  const PopoverContent = props.popoverContent
  const clickable = !!props.onClick || !!PopoverContent

  const isBooked = props.status === 'BOOKED'
  const times = `${props.shift.startTime}-${props.shift.endTime}`

  const { zoom, availableChannels } = useSchedule()
  const { isSelected, toggleSelected, enableSelectShifts } = useSelectedShifts(
    props.shiftIds,
  )

  const isMonth = zoom === 'month'
  const position = isMonth ? 'stretch' : (props.position ?? 'center')

  // const header = <Header>{isBooked ? times : style.label}</Header>
  const header = <Header>{times}</Header>
  // const subtitle = (!isBooked && times) || props.unitName
  const subtitle = style.label || props.unitName

  const channelIndicators =
    props.status === 'VACANT' ? (
      <StyledChannelIndicators
        available={availableChannels}
        used={{
          isPublished: props.isPublished,
          isRequested: false,
          isTendered: props.isTendered,
        }}
      />
    ) : null

  const urgentLabel = (
    <TooltipIcon
      size="sm"
      styles={WARNING_CSS}
      color={Color.LIGHTER_RED}
      backgroundColor={Color.TRANSPARENT}
      design="solid"
      iconName="ExclamationIcon"
      tooltipIntent="warning"
      text={t.shifts.urgent()}
    />
  )

  const absentLabel = (
    <TooltipIcon
      size="sm"
      styles={WARNING_CSS}
      color={Color.WHITE}
      backgroundColor={Color.TRANSPARENT}
      design="solid"
      iconName="PlusCircleIcon"
      tooltipIntent="warning"
      text={t.shifts.absent()}
    />
  )

  const interestedBadge = (
    <AbsoluteBadge background={Color.BLUE} color={Color.WHITE}>
      <span>
        {props.interestedCandidates} / {props.shiftIds.length}
      </span>
    </AbsoluteBadge>
  )

  const countBadge =
    props.shiftIds.length > 1 ? (
      <AbsoluteBadge background={Color.LIGHTEST_BLUE} color={Color.BLUE}>
        <span>× {props.shiftIds.length}</span>
      </AbsoluteBadge>
    ) : null

  const iconAndCheckbox = (
    <>
      <Icon
        className="icon"
        size="sm"
        name={style.icon}
        color={style.iconColor}
      />
      <Div className="checkbox" onClick={(e) => e.stopPropagation()}>
        {props.onDelete ? (
          <Icon
            size="sm"
            name="TrashIcon"
            color={Color.RED}
            onClick={() => props.onDelete?.()}
          />
        ) : (
          <Checkbox size="sm" checked={isSelected} onChange={toggleSelected} />
        )}
      </Div>
    </>
  )

  const content = isMonth ? (
    <MonthContent>{iconAndCheckbox}</MonthContent>
  ) : (
    <>
      <Content>
        {channelIndicators}
        <TextColumn>
          {isBooked && props.isAcknowledged ? (
            <Flex gap={0.5}>
              {header}
              <TooltipIcon
                size="xs"
                iconName="CheckIcon"
                color={style.color}
                backgroundColor={Color.TRANSPARENT}
                text={t.ui.shift_acknowledged_by_candidate()}
              />
            </Flex>
          ) : (
            header
          )}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </TextColumn>
        {iconAndCheckbox}
      </Content>
      {props.shift.onCallTime && (
        <OnCallPadding>
          <ShiftCrossSection
            size="sm"
            shiftHours={style.shiftHours}
            {...props.shift}
          />
        </OnCallPadding>
      )}
      {props.isUrgent && urgentLabel}
      {props.isAbsent && absentLabel}
      {props.interestedCandidates ? interestedBadge : countBadge}
    </>
  )

  const shift = (
    <Card
      {...style}
      position={position}
      as={clickable ? 'button' : 'div'}
      onClick={props.onClick ? () => props.onClick!(props.shiftIds) : undefined}
      hasOnCall={!isMonth && !!props.shift.onCallTime}
      hoverable={clickable}
      selectable={
        !!props.onDelete || (!props.unselectable && enableSelectShifts)
      }
      selected={isSelected}
      isAbsent={props.isAbsent}
      isUrgent={props.isUrgent}
      isAtOtherUnit={props.isAtOtherUnit}
      zoom={zoom}
    >
      {content}
    </Card>
  )

  const withTooltip = props.error ? (
    <RelativeWrapper position={position}>
      {shift}
      {props.error}
    </RelativeWrapper>
  ) : (
    shift
  )

  if (!PopoverContent) return withTooltip

  return (
    <Popover.Root>
      <Popover.Trigger asChild>{withTooltip}</Popover.Trigger>
      <PopoverContentWrapper
        sideOffset={4}
        align="start"
        side="right"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <PopoverContent shiftId={props.shiftIds} />
      </PopoverContentWrapper>
    </Popover.Root>
  )
}
